import { EyeIcon, PencilEdit02Icon, TrashIcon } from '@/icons';
import { errorToast, formatTo12Hour } from '@/utils';
import { Button, Space, type TableProps } from 'antd';
import { useState } from 'react';
import {
  useDeleteShiftMutation,
  useGetAllShiftsQuery,
} from '../../../../app/features/employee/emp-attendance/emp-attendance.api';
import type { ShiftParams } from '../../../../app/features/employee/emp-attendance/emp-attendance.type';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import CustomTable from '../../../../components/CustomTable';
import useAuth from '../../../../hooks/useAuth';
import useError from '../../../../hooks/useError';
import useFilters from '../../../../hooks/useFilters';
import useModal from '../../../../hooks/useModal';
import { toast } from '../../../../lib/toast';
import { addKeyInArray } from '../../../../utils/global.utils';
import AssignShift from './_components/ShiftAndRoaster/AssignShift';
import RosterCalendar from './_components/ShiftAndRoaster/RosterCalendar';
import ShiftDetails from './_components/ShiftAndRoaster/ShiftDetails';

export default function ShiftAndRoaster() {
  const { open, handleOpen, handleClose } = useModal<any>();
  const { tenantId } = useAuth();
  const { filter, limit, page, handlePagination } = useFilters<ShiftParams>();
  const { isLoading, isFetching, error, data } = useGetAllShiftsQuery({
    params: {
      ...filter,
      tenantId,
    },
  });
  useError(error, 'Failed to fetch shift data');
  const [deleteShift, { isLoading: deleteLoading }] = useDeleteShiftMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const shiftList = data?.data?.data || [];
  const shiftMeta = data?.data?.meta;

  // const handleDeleteShift = async (id: string) => {
  //   try {
  //     setDeletingId(id);
  //     await deleteShift({ id }).unwrap();
  //     await refetch();
  //     toast("success", "Shift deleted successfully");
  //     handleClose();
  //   } catch (err) {
  //     errorToast(err, "Failed to delete shift");
  //   } finally {
  //     setDeletingId(null);
  //   }
  // };

  const handleConfirm = async () => {
    const id = open?.data?.id;
    if (!id) return;

    try {
      setDeletingId(id);
      await deleteShift({ id }).unwrap();
      // await refetch();
      toast('success', 'Shift deleted successfully');
      handleClose();
    } catch (err) {
      errorToast(err, 'Failed to delete shift');
    } finally {
      setDeletingId(null);
    }
  };

  const columns: TableProps<object>['columns'] = [
    {
      title: 'Shift Name',
      dataIndex: 'name',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      width: 280,
      align: 'center',
      render: (val) => (val ? formatTo12Hour(val) : 'n/a'),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      width: 280,
      align: 'center',
      render: (val) => (val ? formatTo12Hour(val) : 'n/a'),
    },
    {
      title: 'Grace Period (Min)',
      dataIndex: 'checkInGracePeriod',
      width: 150,
      align: 'center',
      render: (val) => <span className="pl-4">{val ?? 'n/a'}</span>,
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      align: 'end',
      render: (id: string) => (
        <Space>
          <Button
            className="action-btn"
            size="small"
            icon={<EyeIcon size={20} />}
            onClick={() => handleOpen({ type: 'VIEW', data: id })}
          />

          <Button
            className="action-btn"
            size="small"
            icon={<PencilEdit02Icon size={20} />}
            onClick={() => handleOpen({ type: 'UPDATE', data: id })}
          />

          <Button
            className="action-btn"
            size="small"
            icon={<TrashIcon size={20} />}
            loading={deletingId === id}
            disabled={deletingId !== null}
            onClick={() =>
              handleOpen({
                type: 'CONFIRM',
                data: { id }, // store shift id
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        tableRootClassName="!border-0 p-0"
        customTitle="Shift List"
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={addKeyInArray(shiftList)}
        total={shiftMeta?.total || 0}
        hideRowSelect
        onPaginate={handlePagination}
        limit={limit}
        page={page}
      />

      <RosterCalendar />

      <AssignShift
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === 'UPDATE'}
      />

      <ShiftDetails
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === 'VIEW'}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        handleSubmit={handleConfirm}
        open={open.open && open.type === 'CONFIRM'}
        loading={deleteLoading}
        title="Are you sure?"
        subTitle="You want to delete this shift?"
        buttonName="Delete"
      />
    </>
  );
}
